import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../../common/SafeIcon';

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">{value || '—'}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-slate-200">{title}</h4>
    {children}
  </div>
);

export const StepReview = ({ formData }) => {
  const scopeLabels = {
    medical: 'Medical Decisions', school: 'School Enrollment',
    travel: 'Travel Consent', financial: 'Financial Decisions', housing: 'Housing Decisions',
  };

  const conditionLabels = {
    deportation: 'Deportation or Removal Order', detention: 'Immigration Detention',
    extended_absence: 'Extended Absence', incapacitation: 'Incapacitation', other: 'Other',
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
          <SafeIcon icon={FiIcons.FiCheckCircle} className="text-2xl text-emerald-500" />
        </div>
        <h3 className="text-lg font-bold text-brand-navy">Review Your Information</h3>
        <p className="text-sm text-slate-500">Confirm all details before submitting for advisor review.</p>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-sm space-y-2">
        <Section title="Parent / Grantor">
          <ReviewRow label="Full Name" value={formData.parentFullName} />
          <ReviewRow label="Date of Birth" value={formData.parentDOB} />
          <ReviewRow label="Country of Origin" value={formData.parentCountry} />
          <ReviewRow label="Immigration Status" value={formData.parentImmigrationStatus} />
          <ReviewRow label="Address" value={formData.parentAddress} />
        </Section>

        <Section title="Minor Children">
          {(formData.children || []).map((c, i) => (
            <ReviewRow key={i} label={`Child ${i + 1}`} value={`${c.name} | DOB: ${c.dob} | ${c.relationship}`} />
          ))}
        </Section>

        <Section title="Primary Guardian">
          <ReviewRow label="Name" value={formData.guardian1FullName} />
          <ReviewRow label="Relationship" value={formData.guardian1Relationship} />
          <ReviewRow label="Phone" value={formData.guardian1Phone} />
          <ReviewRow label="Accepted Responsibility" value={formData.guardian1Accepts ? 'Yes' : 'No'} />
        </Section>

        <Section title="Alternate Guardian">
          <ReviewRow label="Name" value={formData.guardian2FullName} />
          <ReviewRow label="Relationship" value={formData.guardian2Relationship} />
          <ReviewRow label="Phone" value={formData.guardian2Phone} />
          <ReviewRow label="Accepted Responsibility" value={formData.guardian2Accepts ? 'Yes' : 'No'} />
        </Section>

        <Section title="Scope of Authority">
          <ReviewRow
            label="Granted Powers"
            value={(formData.scopeOfAuthority || []).map((s) => scopeLabels[s]).join(', ')}
          />
        </Section>

        <Section title="Duration & Conditions">
          <ReviewRow label="Effective Date" value={formData.effectiveDate} />
          <ReviewRow label="Expiration" value={formData.expirationOption === 'date' ? formData.expirationDate : 'Until Revoked'} />
          <ReviewRow label="Triggering Condition" value={conditionLabels[formData.triggeringCondition]} />
        </Section>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
        <SafeIcon icon={FiIcons.FiInfo} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          This document will be submitted for review by your assigned advisor. You will be notified once it has been reviewed and finalized.
        </p>
      </div>
    </div>
  );
};